import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAsync } from 'react-async-hook'
import { DEWICATS_COLLECTION } from '@utils/constants'
import { PublicKey, SystemProgram, Transaction } from '@solana/web3.js'
import {
  Metaplex,
  toBigNumber,
  walletAdapterIdentity,
} from '@metaplex-foundation/js'
import { useSolana } from '@dewicats/connect-button'
import { useWallet } from '@solana/wallet-adapter-react'
import { bulkSendRawTransactions, toNumber } from '@helium/spl-utils'
import { useTranslation } from 'react-i18next'
import {
  auctionManagerKey,
  bidderRecieptKey,
  init as initAuctionManager,
  referralRecipientKey,
  meowMetadataKey,
} from '@dewicats/auction-manager-sdk'
import { IdlAccounts, Program } from '@coral-xyz/anchor'
import { AuctionManager } from '@dewicats/idls/lib/types/auction_manager'
import { isPast } from 'date-fns'
import { useWebSocket } from 'next-ws/client'
import { MININUM_BID_INCREMENT } from '@utils/solanaUtils'
import useCollection from './useCollection'
import { NftData, revertAttributes } from '@models/Nft'
import { useMint2022 } from './useOwnedAmount2022'
import { useMetaplexMetadata } from './useMetaplexMetadata'
import {
  NATIVE_MINT,
  createAssociatedTokenAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddress,
  createCloseAccountInstruction,
} from '@solana/spl-token'

type CatAndListing = NftData & {
  listingIndex: number | null
}

type AucionHouseHook = {
  metadata: any
  image: string
  name: string
  minBidIncrement: number
  symbol: string
  currentMinimumBid: string
  bidAmount: number
  setBidAmount: Dispatch<SetStateAction<number>>
  referralCode: string
  setReferralCode: Dispatch<SetStateAction<string>>
  bids: (IdlAccounts<AuctionManager>['bidRecieptV0'] & {
    publicKey: PublicKey
  })[]
  createBid: (
    bidAmount: number,
    referralCodeString?: string,
  ) => Promise<string[]>
  highestBidder: IdlAccounts<AuctionManager>['bidRecieptV0']
  cancelBid: () => Promise<string[]>
  listings: IdlAccounts<AuctionManager>['listingV0'][]
  getListings: () => Promise<
    (IdlAccounts<AuctionManager>['listingV0'] & { publicKey: PublicKey })[]
  >
  currentListing: IdlAccounts<AuctionManager>['listingV0'] & {
    publicKey: PublicKey
  }
  auctionProgram: Program<AuctionManager>
  allCats: CatAndListing[]
  currentCat: CatAndListing
  getAllBidsForWallet: () => Promise<
    (IdlAccounts<AuctionManager>['bidRecieptV0'] & { publicKey: PublicKey })[]
  >
  auctionKey: PublicKey
}

const useAuctionManagerHook = (page?: number): AucionHouseHook => {
  const { t } = useTranslation()
  const { anchorProvider } = useSolana()
  const wallet = useWallet()
  const [allCats, setAllCats] = useState<CatAndListing[]>()
  const { nfts } = useCollection()
  const [bidAmount, setBidAmount] = useState<number>(null)

  const [bids, setBids] =
    useState<
      (IdlAccounts<AuctionManager>['bidRecieptV0'] & { publicKey: PublicKey })[]
    >()
  const [listings, setListings] =
    useState<
      (IdlAccounts<AuctionManager>['listingV0'] & { publicKey: PublicKey })[]
    >()

  const highestBidder = useMemo(() => {
    if (!bids || bids.length === 0) return null

    return bids.reduce((max, bid) => {
      return max.amount.toNumber() > bid.amount.toNumber() ? max : bid
    })
  }, [bids])

  const ws = useWebSocket()

  const metaplex = useMemo(() => {
    return new Metaplex(anchorProvider?.connection)
  }, [anchorProvider])

  metaplex.use(walletAdapterIdentity(anchorProvider))

  const { result: auctionProgram } = useAsync(() => {
    return initAuctionManager(anchorProvider)
  }, [anchorProvider])

  const [auctionKey] = useMemo(
    () => auctionManagerKey(DEWICATS_COLLECTION, 'dewicats'),
    [],
  )

  const getListings = useCallback(async () => {
    if (auctionProgram) {
      let l = (
        await auctionProgram.account.listingV0.all([
          {
            memcmp: {
              offset: 8,
              bytes: auctionKey.toBase58(),
            },
          },
        ])
      )
        .map((i) => ({
          ...i.account,
          publicKey: i.publicKey,
        }))
        // sort listings by start date in ascending order
        .sort((a, b) => a.createdAt.toNumber() - b.createdAt.toNumber())

      // Remove the last listing if the second to last has not ended
      if (
        l.length > 1 &&
        !isPast(new Date(l[l.length - 2].endAt.toNumber() * 1000))
      ) {
        l.pop()
      }

      setListings(l)
      return l
    }
  }, [auctionProgram, auctionKey])

  useEffect(() => {
    if (!nfts) return
    if (!listings) return

    const cats = nfts
      .map((nft) => ({
        ...nft,
        listingIndex: listings.findIndex((l) => l.nft.toBase58() === nft.id),
      }))
      .reverse()

    // Remove the last cat if the second to last has not ended
    const secondToLast = cats[cats.length - 2]
    const hasSecondToLastListing = secondToLast?.listingIndex !== -1
    if (hasSecondToLastListing) {
      const secondToLastListing = listings[secondToLast.listingIndex]
      const auctionEndDate = new Date(
        secondToLastListing.endAt.toNumber() * 1000,
      )
      if (!isPast(auctionEndDate)) {
        cats.pop()
      }
    }

    // Remove the last cat if the last cat is not a listing
    const lastCat = cats[cats.length - 1]
    if (lastCat.listingIndex === -1) {
      cats.pop()
    }

    setAllCats(cats)
  }, [listings, nfts])

  const realPageIndex = useMemo(() => {
    if (!listings?.length) return 1
    return page === undefined ? allCats?.length - 1 : page - 1
  }, [allCats, listings, page])

  const currentListing = useMemo(() => {
    const listingIndex = allCats?.[realPageIndex]?.listingIndex

    if (listingIndex === null) return

    return listings?.[listingIndex]
  }, [listings, realPageIndex, allCats])

  const { symbol } = useMetaplexMetadata(currentListing?.tokenMint)

  const minBidIncrement = useMemo(
    () => MININUM_BID_INCREMENT[symbol] || MININUM_BID_INCREMENT.DEFAULT,
    [symbol],
  )

  const currentCat = useMemo(() => {
    return allCats?.[realPageIndex]
  }, [realPageIndex, allCats])

  const { info } = useMint2022(
    currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
      ? undefined
      : currentListing?.tokenMint,
  )

  const currentBid = useMemo(() => {
    if (!bids || bids?.length === 0) return undefined
    return toNumber(
      highestBidder.amount,
      currentListing?.tokenMint?.toBase58() === NATIVE_MINT.toBase58()
        ? 9
        : info?.decimals || 6,
    )
  }, [bids, highestBidder?.amount, info, currentListing])

  const [referralCode, setReferralCode] = useState<string>(null)

  const [currentMinimumBid, setCurrentMinimumBid] = useState(() => {
    if (!currentListing) {
      return '-'
    }

    if (!currentBid) {
      const newValue = currentListing.startingPrice.toNumber() + minBidIncrement
      return `${newValue}`
    }

    const newValue = currentBid + minBidIncrement
    return newValue.toLocaleString()
  })

  useEffect(() => {
    const timeout = setTimeout(
      () =>
        setCurrentMinimumBid((prev) => {
          if (!currentListing || !currentBid) return prev

          const newValue = currentBid + minBidIncrement
          return newValue.toLocaleString()
        }),
      500,
    )
    return () => clearTimeout(timeout)
  }, [currentBid, currentListing, minBidIncrement])

  const getBids = useCallback(async () => {
    if (auctionProgram && currentListing) {
      return (
        await auctionProgram.account.bidRecieptV0.all([
          {
            memcmp: {
              offset: 8,
              bytes: currentListing.publicKey.toBase58(),
            },
          },
        ])
      ).map((i) => ({
        ...i.account,
        publicKey: i.publicKey,
      }))
    }
  }, [auctionProgram, currentListing])

  const getAllBidsForWallet = useCallback(async () => {
    if (auctionProgram && wallet) {
      const x = (
        await auctionProgram.account.bidRecieptV0.all([
          {
            memcmp: {
              offset: 8 + 32,
              bytes: wallet.publicKey.toBase58(),
            },
          },
        ])
      ).map((i) => ({
        ...i.account,
        publicKey: i.publicKey,
      }))
      return x
    }
  }, [auctionProgram, wallet])

  const onMessage = useCallback(
    async (event: MessageEvent<Blob>) => {
      try {
        try {
          const newBids = await getBids()
          setBids(newBids)
        } catch {}
      } catch (e) {
        console.error(e)
      }
    },
    [getBids],
  )

  const onOpen = useCallback(async () => {
    const request = {
      jsonrpc: '2.0',
      id: 420,
      method: 'accountSubscribe',
      params: [
        currentListing?.publicKey?.toBase58(), // pubkey of account we want to subscribe to
        {
          encoding: 'jsonParsed', // base58, base64, base65+zstd, jsonParsed
          commitment: 'confirmed', // defaults to finalized if unset
        },
      ],
    }
    await new Promise((resolve, reject) => {
      resolve(ws?.send(JSON.stringify(request)))
    })
  }, [currentListing, ws])

  useEffect(() => {
    if (!currentListing) return

    const auctionEndDate = new Date(currentListing?.endAt.toNumber() * 1000)

    // check if the auction has ended
    if (isPast(auctionEndDate)) {
      return
    }

    ws?.readyState === WebSocket.OPEN && onOpen()
    ws?.addEventListener('open', onOpen)
    ws?.addEventListener('message', onMessage)
    return () => {
      // ws?.removeEventListener('open', onOpen)
      ws?.removeEventListener('message', onMessage)
    }
  }, [currentListing, onMessage, onOpen, ws])

  useAsync(async () => {
    const initialBids = await getBids()
    setBids(initialBids)
  }, [getBids])

  const createBid = useCallback(
    async (bidAmount: number, referralCodeString?: string) => {
      if (!wallet) return

      let bidRecieptAcc: IdlAccounts<AuctionManager>['bidRecieptV0']
      let referralRecipientAcc: IdlAccounts<AuctionManager>['referralRecipientV0']
      let meowMetadataAcc: IdlAccounts<AuctionManager>['meowMetadataV0']

      let referralRecipient
      let meowMetadata
      const bidReciept = bidderRecieptKey(
        currentListing.publicKey,
        anchorProvider.publicKey,
      )[0]
      if (referralCodeString) {
        referralRecipient = referralRecipientKey(
          currentListing.publicKey,
          referralCodeString,
        )[0]
      }

      try {
        bidRecieptAcc =
          await auctionProgram.account.bidRecieptV0.fetch(bidReciept)
      } catch (e) {
        console.error(e)
      }

      if (referralRecipient) {
        try {
          referralRecipientAcc =
            await auctionProgram.account.referralRecipientV0.fetch(
              referralRecipient,
            )
        } catch (e) {
          console.error(e)
        }
      }

      const nft = nfts.find((nft) => nft.name === referralCodeString)

      if (nft) {
        meowMetadata = meowMetadataKey(new PublicKey(nft.id), auctionKey)[0]

        try {
          meowMetadataAcc =
            await auctionProgram.account.meowMetadataV0.fetch(meowMetadata)
        } catch (e) {
          console.error(e)
        }
      }
      const txn = new Transaction()

      if (!bidRecieptAcc) {
        const initBidIx = await auctionProgram.methods
          .initializeBidRecieptV0({
            amount: toBigNumber(0),
          })
          .accounts({
            listing: currentListing.publicKey,
            payer: anchorProvider.publicKey,
          })
          .instruction()
        txn.add(initBidIx)
      }

      if (!referralRecipientAcc && nft && referralRecipient) {
        const initReferralIx = await auctionProgram.methods
          .initializeReferralRecipientV0({
            nftName: referralCodeString,
          })
          .accountsPartial({
            listing: currentListing.publicKey,
            payer: anchorProvider.publicKey,
            auctionManager: auctionKey,
            referralRecipient,
            nft: new PublicKey(nft.id),
          })
          .instruction()
        txn.add(initReferralIx)
      }

      const placeBidIx = await auctionProgram.methods
        .placeBidV0({
          amount: toBigNumber(
            bidAmount *
              10 **
                (currentListing?.tokenMint?.toBase58() ===
                NATIVE_MINT.toBase58()
                  ? 9
                  : info.decimals),
          ),
        })
        .accountsPartial({
          listing: currentListing.publicKey,
          tokenMint: currentListing.tokenMint,
          auctionManager: auctionKey,
          bidReciept,
          payer: anchorProvider.publicKey,
          referralRecipient: referralRecipient ? referralRecipient : null,
          meowMetadata: meowMetadataAcc ? meowMetadata : null,
        })
        .instruction()

      // If SOL listing we need to wrap SOL to a SOL token account
      if (currentListing.tokenMint.toBase58() === NATIVE_MINT.toBase58()) {
        const associatedTokenAccount = await getAssociatedTokenAddress(
          NATIVE_MINT,
          anchorProvider.publicKey,
          true,
        )

        try {
          const accountInfo = await anchorProvider.connection.getAccountInfo(
            associatedTokenAccount,
          )

          if (!accountInfo) {
            txn.add(
              createAssociatedTokenAccountInstruction(
                anchorProvider.publicKey,
                associatedTokenAccount,
                anchorProvider.publicKey,
                NATIVE_MINT,
              ),
            )
          }
        } catch {
          txn.add(
            createAssociatedTokenAccountInstruction(
              anchorProvider.publicKey,
              associatedTokenAccount,
              anchorProvider.publicKey,
              NATIVE_MINT,
            ),
          )
        }

        txn.add(
          SystemProgram.transfer({
            fromPubkey: anchorProvider.publicKey,
            toPubkey: associatedTokenAccount,
            lamports: BigInt(
              bidAmount *
                10 **
                  (currentListing?.tokenMint?.toBase58() ===
                  NATIVE_MINT.toBase58()
                    ? 9
                    : info.decimals),
            ),
          }),
        )

        txn.add(createSyncNativeInstruction(associatedTokenAccount))
      }

      txn.add(placeBidIx)

      txn.feePayer = anchorProvider.publicKey
      txn.recentBlockhash = (
        await anchorProvider.connection.getLatestBlockhash()
      ).blockhash

      const signedTxn = await anchorProvider.wallet.signTransaction(txn)

      const sigs = await bulkSendRawTransactions(anchorProvider.connection, [
        signedTxn.serialize(),
      ])

      if (!sigs || sigs.length === 0) {
        throw new Error('Transaction Error')
      }

      const newBids = await getBids()
      setBids(newBids)
      return sigs
    },
    [
      wallet,
      currentListing,
      anchorProvider,
      auctionProgram,
      info,
      auctionKey,
      getBids,
      nfts,
    ],
  )

  const cancelBid = useCallback(async () => {
    if (!wallet) {
      throw new Error(t('auction.errors.auctionHouse.cancelBid'))
    }

    const bidReciept = bidderRecieptKey(
      currentListing.publicKey,
      anchorProvider.publicKey,
    )[0]

    const listingAcc = await auctionProgram.account.listingV0.fetch(
      currentListing.publicKey,
    )

    const cancelBidIx = await auctionProgram.methods
      .cancelBidV0({
        bidReciept,
      })
      .accountsPartial({
        listing: currentListing.publicKey,
        bidReciept,
        payer: anchorProvider.publicKey,
        tokenMint: listingAcc.tokenMint,
        auctionManager: auctionKey,
      })
      .instruction()

    const txn = new Transaction()

    txn.add(cancelBidIx)

    if (currentListing.tokenMint.toBase58() === NATIVE_MINT.toBase58()) {
      const associatedTokenAccount = await getAssociatedTokenAddress(
        NATIVE_MINT,
        anchorProvider.publicKey,
      )
      txn.add(
        createCloseAccountInstruction(
          associatedTokenAccount,
          anchorProvider.publicKey,
          anchorProvider.publicKey,
        ),
      )
    }

    txn.feePayer = anchorProvider.publicKey
    txn.recentBlockhash = (
      await anchorProvider.connection.getLatestBlockhash()
    ).blockhash

    const signedTx = await wallet.signTransaction(txn)

    const sigs = await bulkSendRawTransactions(anchorProvider?.connection, [
      signedTx.serialize(),
    ])

    if (!sigs || sigs.length === 0) {
      throw new Error('Transaction Error')
    }

    return sigs
  }, [wallet, currentListing, anchorProvider, auctionProgram, auctionKey, t])

  const image = useMemo(() => {
    return currentCat?.imageURL
  }, [currentCat])

  const metadata = useMemo(() => {
    if (!currentCat?.attribute) return
    return revertAttributes(currentCat?.attribute)
  }, [currentCat])

  const name = useMemo(() => {
    return currentCat?.name
  }, [currentCat])

  return {
    name,
    metadata,
    image,
    minBidIncrement,
    symbol,
    currentMinimumBid,
    highestBidder,
    bids,
    bidAmount,
    setBidAmount,
    referralCode,
    setReferralCode,
    createBid,
    cancelBid,
    listings,
    getListings,
    currentListing,
    auctionProgram,
    allCats,
    currentCat,
    getAllBidsForWallet,
    auctionKey,
  }
}

export type AuctionContextManager = ReturnType<typeof useAuctionManagerHook>

const AuctionManagerContext = createContext<AuctionContextManager | null>(null)

const { Provider } = AuctionManagerContext

const AuctionManagerProvider = ({
  page,
  children,
}: {
  page?: number
  children: ReactNode
}) => {
  return <Provider value={useAuctionManagerHook(page)}>{children}</Provider>
}

const useAuctionManager = (): AuctionContextManager => {
  const context = useContext(AuctionManagerContext)
  if (!context) {
    throw new Error(
      'useAuctionManager has to be used within <AuctionManagerProvider>',
    )
  }
  return context
}
export { useAuctionManager, AuctionManagerProvider }

export default useAuctionManager
